/**
 * How a session is delivered, orthogonal to the per-question text/voice axis.
 *
 * `chat` is the responder reading and answering at their own pace; `handsFree`
 * is the AI moderating aloud. It is deliberately *not* a third `ResponseMode`:
 * `RESPONSE_MODES` is `INTERACTION_MODES` from `@/shared-question-model` and is
 * overridable per question, so a third member would silently change
 * `showsContinueButton`, `resolveComposerVariant` and `shouldKeepMicrophoneLive`
 * for every kind. It is not a `ThreadKind` either, because `THREAD_KINDS` is
 * `QUESTION_KINDS` — a new member would appear in the builder's question-type
 * picker — nor a phase, because hands-free spans DeviceGate through Outro.
 */
export const DELIVERY_CHANNELS = {
  Chat: 'chat',
  HandsFree: 'handsFree',
} as const;

export type DeliveryChannel =
  (typeof DELIVERY_CHANNELS)[keyof typeof DELIVERY_CHANNELS];

/**
 * The hands-free loop's own status, reduced beside `responderSessionReducer`
 * rather than inside it: playback ending, voice onset and silence are timing
 * facts with no question-domain meaning, and folding them into the session
 * reducer would make every existing session spec carry audio state.
 */
export const HANDS_FREE_STATUSES = {
  Dormant: 'dormant',
  Pending: 'pending',
  Speaking: 'speaking',
  Listening: 'listening',
  Capturing: 'capturing',
  Captured: 'captured',
  Paused: 'paused',
  Ended: 'ended',
} as const;

export type HandsFreeStatus =
  (typeof HANDS_FREE_STATUSES)[keyof typeof HANDS_FREE_STATUSES];

export const HANDS_FREE_DELIVERY_STATUSES: ReadonlySet<HandsFreeStatus> =
  new Set([
    HANDS_FREE_STATUSES.Pending,
    HANDS_FREE_STATUSES.Speaking,
    HANDS_FREE_STATUSES.Listening,
  ]);

export interface HandsFreeState {
  readonly status: HandsFreeStatus;
  /**
   * The last script handed to playback. Compared against the script the session
   * currently implies, so a `Pending` turn re-arms the microphone instead of
   * speaking the same clip again.
   */
  readonly spokenScriptKey: string | null;
  readonly transcript: string | null;
  readonly pausedFrom: HandsFreeStatus | null;
}

/**
 * Where a spoken line comes from: the question's own prompt, or an assistant
 * turn's text. Both are project or backend data the kernel already holds.
 */
export const HANDS_FREE_SCRIPT_SLOTS = {
  QuestionPrompt: 'questionPrompt',
  TurnText: 'turnText',
} as const;

type HandsFreeScriptSlot =
  (typeof HANDS_FREE_SCRIPT_SLOTS)[keyof typeof HANDS_FREE_SCRIPT_SLOTS];

export interface HandsFreeScript {
  readonly cacheKey: string;
  readonly slot: HandsFreeScriptSlot;
  readonly text: string;
}

/**
 * A kind's spoken affordances. Derived from the shared capability table so all
 * ten existing kind files stay byte-identical, and overridable per kind through
 * `ThreadBehaviour.spokenAffordances`.
 */
export interface SpokenAffordances {
  /** Read the option list aloud after the question. */
  readonly announcesOptions: boolean;
  readonly draftsSpokenAnswer: boolean;
  /** Advancing needs the spoken Continue command rather than a recognised pick. */
  readonly awaitsSpokenContinue: boolean;
  readonly extractsSpokenPick: boolean;
}
